import { useMemo } from 'react';

import { useClusters } from '@cast/shared-cluster-context-data-access';
import { useOrganizationOverviewQuery } from '@cast/shared-cost-data-access';
import type { ClusterOptimization, ClusterOverviewItem } from '@cast/types';
import { AgentStatus, ClusterDisplayStatus } from '@cast/types';

import {
  getMonthlyCostFromSummary,
  getSloMetricById,
  mapOverviewClusterToSummaryNormalized,
  mapOverviewSloToColumnData,
  getMonthlySavingsFromOverview,
  isOverviewClusterDiscovered,
  type SloColumnData,
  type SloMetric,
} from '@cast/features-cluster-shared-utils';
import {
  getClustersOverviewRangeIso,
  useClustersOverviewDateRange,
} from './useClustersOverviewDateRange';
import { useClusterOptimizationsQuery } from './useClusterOptimizationsQuery';

export type ClusterListRowData = {
  summary?: ReturnType<typeof mapOverviewClusterToSummaryNormalized>;
  isSummaryLoading: boolean;
  monthlyCost: number | null;
  monthlySavings: number | null;
  isSavingsLoading: boolean;
  slo: SloColumnData;
  isSloLoading: boolean;
  isDiscovered: boolean;
  score?: number;
  isScoreLoading: boolean;
  isScoreEvaluating: boolean;
  workloadAutoscalerEnabled: boolean;
  nodeAutoscalerEnabled: boolean;
  isAutoscalerStatusLoading: boolean;
  getSloMetric: (id: string) => SloMetric | undefined;
};

export const useClustersListRowData = () => {
  const { clusters, isLoading: isClustersLoading } = useClusters({
    polling: true,
  });
  const range = useClustersOverviewDateRange();
  const { startTime, endTime } = getClustersOverviewRangeIso(range);
  const { data: overview, isLoading: isOverviewQueryLoading } =
    useOrganizationOverviewQuery({ startTime, endTime });
  const {
    data: clusterOptimizations,
    isLoading: isClusterOptimizationsLoading,
  } = useClusterOptimizationsQuery();

  const overviewClusterMap = useMemo(() => {
    const map = new Map<string, ClusterOverviewItem>();

    overview?.clusters?.forEach((item) => {
      if (item.clusterId) {
        map.set(item.clusterId, item);
      }
    });

    return map;
  }, [overview?.clusters]);

  const optimizationsByClusterId = useMemo(() => {
    const map = new Map<string, ClusterOptimization>();

    clusterOptimizations?.forEach((item) => {
      if (item.clusterId) {
        map.set(item.clusterId, item);
      }
    });

    return map;
  }, [clusterOptimizations]);

  const rowDataByClusterId = useMemo(() => {
    const map = new Map<string, ClusterListRowData>();

    clusters.forEach((cluster) => {
      if (!cluster.id) {
        return;
      }

      const overviewItem = overviewClusterMap.get(cluster.id);
      const isDiscovered =
        cluster.displayStatus === ClusterDisplayStatus.DISCOVERED ||
        isOverviewClusterDiscovered(overviewItem);
      const summary = overviewItem
        ? mapOverviewClusterToSummaryNormalized(overviewItem)
        : undefined;
      const isSummaryLoading = isOverviewQueryLoading && !summary;
      const monthlySavings = getMonthlySavingsFromOverview(overviewItem);
      const monthlyCost = getMonthlyCostFromSummary(summary);
      const slo = mapOverviewSloToColumnData(overviewItem?.slo);
      const isSloLoading = isOverviewQueryLoading && !overviewItem;

      const optimization = optimizationsByClusterId.get(cluster.id);
      const score = isDiscovered
        ? undefined
        : optimization?.efficiency?.score ?? undefined;
      const isScoreLoading = isClusterOptimizationsLoading && score == null;
      const isScoreEvaluating =
        !isDiscovered &&
        cluster.agentStatus === AgentStatus.ONLINE &&
        (score == null || score === 0);
      const workloadAutoscalerEnabled = isDiscovered
        ? false
        : !!optimization?.workloadAutoscaler?.enabled;
      const nodeAutoscalerEnabled = isDiscovered
        ? false
        : !!optimization?.infrastructureAutoscaler?.enabled;
      const isAutoscalerStatusLoading =
        !isDiscovered && isClusterOptimizationsLoading && optimization == null;

      map.set(cluster.id, {
        summary,
        isSummaryLoading,
        monthlyCost,
        monthlySavings: monthlySavings ?? null,
        isSavingsLoading: isOverviewQueryLoading,
        slo,
        isSloLoading,
        isDiscovered,
        score,
        isScoreLoading,
        isScoreEvaluating,
        workloadAutoscalerEnabled,
        nodeAutoscalerEnabled,
        isAutoscalerStatusLoading,
        getSloMetric: (id: string) => getSloMetricById(slo.metrics, id),
      });
    });

    return map;
  }, [
    clusters,
    isOverviewQueryLoading,
    overviewClusterMap,
    isClusterOptimizationsLoading,
    optimizationsByClusterId,
  ]);

  const getRowData = (clusterId: string) => rowDataByClusterId.get(clusterId);

  return {
    clusters,
    isLoading: isClustersLoading,
    getRowData,
  };
};
